import { createEffect, createSignal, For, type JSX, Show } from 'solid-js';
import { Portal } from 'solid-js/web';

// ─── Icons: lucide's strokes, drawn here (the page loads nothing but its own bundle, ADR-0150) ────────────────────

const ICONS = {
  users: '<circle cx="9" cy="8" r="4"/><path d="M2 21a7 7 0 0 1 14 0M16 3.2a4 4 0 0 1 0 7.6M22 21a7 7 0 0 0-4.5-6.5"/>',
  user: '<circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/>',
  activity: '<path d="M3 12h4l3-8 4 16 3-8h4"/>',
  sparkles:
    '<path d="m12 3 1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z"/><path d="m19 15 .8 1.7 1.7.8-1.7.8L19 21l-.8-1.7-1.7-.8 1.7-.8z"/>',
  key: '<circle cx="7.5" cy="15.5" r="4.5"/><path d="M10.7 12.3 21 2M16 7l3 3M19 4l2 2"/>',
  twitch: '<path d="M4 3h16v11l-4 4h-4l-3 3v-3H4z"/><path d="M11 8v4M15 8v4"/>',
  trophy:
    '<path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0z"/><path d="M17 5h3v2a3 3 0 0 1-3 3M7 5H4v2a3 3 0 0 0 3 3"/>',
  search: '<circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/>',
  copy: '<rect x="9" y="9" width="12" height="12" rx="2"/><path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1"/>',
  check: '<path d="m5 12 5 5L20 7"/>',
  x: '<path d="M6 6l12 12M18 6 6 18"/>',
  logout: '<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"/>',
  sound: '<path d="M11 5 6 9H2v6h4l5 4z"/><path d="M15.5 8.5a5 5 0 0 1 0 7M19 5a10 10 0 0 1 0 14"/>',
  mute: '<path d="M11 5 6 9H2v6h4l5 4z"/><path d="m22 9-6 6M16 9l6 6"/>',
  refresh: '<path d="M21 12a9 9 0 1 1-2.6-6.4L21 8M21 3v5h-5"/>',
  camera:
    '<path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3z"/><circle cx="12" cy="13" r="3"/>',
  plus: '<path d="M12 5v14M5 12h14"/>',
  trash: '<path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3"/>',
  power: '<path d="M12 3v9M6.3 6.3a8 8 0 1 0 11.4 0"/>',
  arrow: '<path d="M5 12h14M13 6l6 6-6 6"/>',
  external: '<path d="M15 3h6v6M10 14 21 3M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>',
  flag: '<path d="M5 22V4M5 4h12l-2 4.5L17 13H5"/>',
  chevron: '<path d="m6 9 6 6 6-6"/>',
  leaf: '<path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.5 19 2c1 2 2 4.2 2 8 0 5.5-4.8 10-10 10Z"/><path d="M2 21c0-3 1.9-5.4 5.1-6.1C9.5 14.4 12 13 13 12"/>',
  menu: '<path d="M4 6h16M4 12h16M4 18h16"/>',
  danger:
    '<path d="m21.7 18-8-14a2 2 0 0 0-3.4 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.7-3Z"/><path d="M12 9v4M12 17h.01"/>',
  film: '<rect x="5" y="2" width="14" height="20" rx="2"/><path d="m10 9 5 3-5 3z"/>',
  map: '<path d="M14.1 5.6a2 2 0 0 0 1.8 0l3.7-1.9a1 1 0 0 1 1.4.9v12.8a1 1 0 0 1-.6.9l-4.5 2.3a2 2 0 0 1-1.8 0l-4.2-2.1a2 2 0 0 0-1.8 0l-3.7 1.9a1 1 0 0 1-1.4-.9V6.6a1 1 0 0 1 .6-.9l4.5-2.3a2 2 0 0 1 1.8 0z"/><path d="M15 5.8v15M9 3.2v15"/>',
  back: '<path d="M19 12H5M11 18l-6-6 6-6"/>',
  minus: '<path d="M5 12h14"/>',
  monitor: '<rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/>',
  eye: '<path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"/><circle cx="12" cy="12" r="3"/>',
  play: '<path d="M7 4v16l13-8z"/>',
  pause: '<path d="M7 4h3v16H7zM14 4h3v16h-3z"/>',
  frame: '<path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2"/>',
} as const;
export type IconName = keyof typeof ICONS;

export function Icon(props: { name: IconName; size?: number; class?: string }) {
  return (
    <svg
      class={props.class}
      width={props.size ?? 16}
      height={props.size ?? 16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
      innerHTML={ICONS[props.name]}
    />
  );
}

// ─── Toasts: what happened, briefly; what failed, a while longer ───────────────────────────────────────────────

interface Toast {
  id: number;
  kind: 'ok' | 'error';
  text: string;
}
const [toasts, setToasts] = createSignal<Toast[]>([]);
let toastIds = 0;
const dismiss = (id: number) => setToasts((t) => t.filter((x) => x.id !== id));

/** Says `text` in the corner: a success for 3 s, a failure for 7 s. */
export function toast(text: string, kind: Toast['kind'] = 'ok'): void {
  const id = ++toastIds;
  setToasts((t) => [...t.slice(-3), { id, kind, text }]);
  setTimeout(() => dismiss(id), kind === 'error' ? 7000 : 3000);
}

/** logdash's toaster (`Toaster.svelte`): an alert with its colour down the side and a bar that runs out. */
export function Toasts() {
  return (
    <div
      class="fixed right-8 bottom-4 z-[1000] flex w-max max-w-[calc(100vw-2rem)] flex-col gap-2"
      role="status"
      aria-live="polite"
    >
      <For each={toasts()}>
        {(t) => (
          <div class="ld-alert border-surface-elevated-border relative flex w-80 max-w-[320px] overflow-hidden rounded-xl px-3 pr-10 shadow-lg">
            <div class="flex w-full shrink-0 items-start justify-start gap-4">
              <div
                class={`max-w-1.5 min-w-1.5 flex-1 shrink-0 self-stretch rounded-full ${t.kind === 'ok' ? 'bg-success' : 'bg-error'}`}
              />
              <div class="flex flex-col justify-start gap-0.5 py-0.5">
                <span class="text-fg-secondary flex-1 whitespace-pre-wrap">{t.text}</span>
              </div>
              <button
                type="button"
                class="ld-button absolute top-3 right-3"
                data-variant="ghost"
                data-size="xs"
                data-shape="circle"
                aria-label="Dismiss"
                onClick={() => dismiss(t.id)}
              >
                <Icon name="x" class="size-5" />
              </button>
            </div>
            <div
              class={`absolute bottom-0 left-0 h-0.5 w-full origin-left ${t.kind === 'ok' ? 'bg-success' : 'bg-error'}`}
              style={{ animation: `ld-toast-run ${t.kind === 'error' ? 7000 : 3000}ms linear forwards` }}
            />
          </div>
        )}
      </For>
    </div>
  );
}

/**
 * An optimistic change: `apply` shows it at once and returns how to take it back, then `send` tells the server. A
 * failure takes it back and says why; `done` gets the server's answer. True once the server took it.
 */
export async function optimistic<T>(
  apply: () => () => void,
  send: () => Promise<T>,
  done?: (answer: T) => void,
): Promise<boolean> {
  const undo = apply();
  try {
    done?.(await send());
    return true;
  } catch (err) {
    undo();
    toast((err as Error).message, 'error');
    return false;
  }
}

// ─── Dialogs: confirm, or type something first (a reason, a name) ────────────────────────────────────────────

interface Ask {
  title: string;
  body?: string;
  confirm: string;
  danger?: boolean;
  /** A field to fill first: its label; `match`, what must be typed (any case) for the button to work. */
  input?: { label: string; placeholder?: string; match?: string };
}
const [asking, setAsking] = createSignal<(Ask & { done: (answer: string | null) => void }) | null>(null);

/** Asks the admin to confirm (`Ask`): what they typed ('' with no field), or null when they cancelled. */
export const ask = (a: Ask): Promise<string | null> => new Promise((done) => setAsking({ ...a, done }));

/** logdash's modal (`Modal.svelte`): an elevated card in the middle over the page, dimmed. */
export function Dialogs() {
  let dialog!: HTMLDialogElement;
  const [text, setText] = createSignal('');
  const ready = () => {
    const field = asking()?.input;
    if (!field) return true;
    return field.match ? text().trim().toUpperCase() === field.match.toUpperCase() : text().trim() !== '';
  };
  const close = (answer: string | null) => {
    const a = asking();
    setAsking(null);
    if (dialog.open) dialog.close();
    a?.done(answer);
  };
  createEffect(() => {
    if (!asking()) return;
    setText('');
    dialog.showModal();
  });
  return (
    <dialog
      ref={dialog}
      class="fixed inset-0 z-999 m-0 flex h-full max-h-none w-full max-w-none overflow-y-auto border-0 bg-transparent p-4 backdrop:bg-transparent [&:not([open])]:hidden"
      onCancel={(e) => {
        e.preventDefault();
        close(null);
      }}
    >
      <Show when={asking()}>
        {(a) => (
          <form
            class="bg-surface-elevated-bg border-surface-elevated-border relative z-10 m-auto flex w-110 max-w-full flex-col gap-4 rounded-xl border p-5"
            onSubmit={(e) => {
              e.preventDefault();
              if (ready()) close(text().trim());
            }}
          >
            <div class="flex flex-col gap-1">
              <h2 class="text-base font-semibold">{a().title}</h2>
              <Show when={a().body}>
                <p class="text-fg-tertiary text-sm">{a().body}</p>
              </Show>
            </div>
            <Show when={a().input}>
              {(field) => (
                <label class="flex flex-col gap-1.5">
                  <span class="text-fg-secondary text-sm font-medium">{field().label}</span>
                  <input
                    autofocus
                    class="ld-input ld-input-padding h-10"
                    value={text()}
                    placeholder={field().placeholder ?? ''}
                    onInput={(e) => setText(e.currentTarget.value)}
                  />
                </label>
              )}
            </Show>
            <div class="flex justify-end gap-2">
              <button type="button" class="ld-button" data-size="sm" onClick={() => close(null)}>
                Cancel
              </button>
              <button
                type="submit"
                class="ld-button"
                data-size="sm"
                data-variant={a().danger ? 'danger' : 'primary'}
                disabled={!ready()}
              >
                {a().confirm}
              </button>
            </div>
          </form>
        )}
      </Show>
      <div class="bg-surface-root-bg/60 fixed inset-0" aria-hidden="true" onClick={() => close(null)} />
    </dialog>
  );
}

// ─── Small pieces ────────────────────────────────────────────────────────────────────────────────────────────

const relative = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
const STEPS: readonly [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 31_536_000_000],
  ['month', 2_592_000_000],
  ['week', 604_800_000],
  ['day', 86_400_000],
  ['hour', 3_600_000],
  ['minute', 60_000],
];
/** `3 hours ago`, `yesterday`, `just now`. */
export function ago(iso: string, now = Date.now()): string {
  const ms = new Date(iso).getTime() - now;
  for (const [unit, size] of STEPS) if (Math.abs(ms) >= size) return relative.format(Math.round(ms / size), unit);
  return 'just now';
}
/** A date and time in this browser's words. */
export const when = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
/** 40000 → `40,000`. */
export const count = (n: number): string => n.toLocaleString('en');

/** A text copied on a click (an id, a link): logdash's `IconButton` (`well`: inside a Well, its hover so). */
export function Copy(props: { text: string; label?: string; well?: boolean; class?: string }) {
  return (
    <button
      type="button"
      class={`text-fg-muted transition-ink hover:text-fg-default focus-visible:outline-brand relative inline-flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md align-middle focus-visible:outline-2 ${props.well ? 'hover:bg-surface-25-hover-bg' : 'hover:bg-surface-100-hover-bg'} ${props.class ?? ''}`}
      title={props.label ?? 'Copy'}
      aria-label={props.label ?? 'Copy'}
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(props.text).then(
          () => toast('Copied'),
          () => toast('Could not copy', 'error'),
        );
      }}
    >
      <Icon name="copy" class="size-3.5" />
    </button>
  );
}

/** On or off: logdash's checkbox (`ld-checkbox`, the primary one), as its settings toggle things. */
export function Switch(props: {
  checked: boolean;
  onChange: (on: boolean) => void;
  label: string;
  disabled?: boolean;
  size?: 'xs';
  id?: string;
}) {
  return (
    <input
      type="checkbox"
      id={props.id}
      class="ld-checkbox"
      data-variant="primary"
      data-size={props.size}
      aria-label={props.label}
      checked={props.checked}
      disabled={props.disabled}
      onChange={(e) => {
        const on = e.currentTarget.checked;
        // The parent decides (it may ask first): the box shows what it says, not the click.
        e.currentTarget.checked = props.checked;
        props.onChange(on);
      }}
    />
  );
}

/** One of a few, side by side: logdash's `SegmentedControl` (the picked one raised on a well). */
export function Segmented<T extends string>(props: {
  value: T;
  options: readonly (readonly [T, string])[];
  onChange: (value: T) => void;
  size?: 'sm' | 'xs';
}) {
  return (
    <div class="ld-seg" data-size={props.size ?? 'sm'}>
      <For each={props.options}>
        {([value, label]) => (
          <button
            type="button"
            class="ld-seg-pill"
            aria-pressed={props.value === value}
            onClick={() => props.value !== value && props.onChange(value)}
          >
            {label}
          </button>
        )}
      </For>
    </div>
  );
}

// ─── logdash's building blocks (apps/frontend's shared components), as they are ─────────────────────────────────

/** logdash's top-bar controls (`toolbar.ts`): a pill, and a round icon button, both outlined by a hairline. */
export const TOOLBAR_CONTROL =
  'hover:text-fg-default transition-ink focus-visible:outline-brand flex h-8 shrink-0 cursor-pointer items-center gap-1.5 rounded-full px-3 text-sm edge focus-visible:outline-2';
export const TOOLBAR_ICON_CONTROL =
  'text-fg-tertiary hover:text-fg-default transition-ink focus-visible:outline-brand flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full edge focus-visible:outline-2';

const TOOLBAR_GROUP = 'flex h-8 shrink-0 items-center gap-0.5 rounded-full p-0.5 edge';
const TOOLBAR_GROUP_OPTION =
  'transition-ink focus-visible:outline-brand flex h-7 cursor-pointer items-center gap-1.5 rounded-full px-3 text-sm focus-visible:outline-2';

/** One of a few in the top bar: logdash's toolbar group (`TOOLBAR_GROUP`), the picked option raised; `count` beside a label. */
export function ToolbarGroup<T extends string>(props: {
  value: T;
  options: readonly (readonly [T, string, number?])[];
  onChange: (value: T) => void;
  label: string;
}) {
  return (
    <fieldset class={`${TOOLBAR_GROUP} m-0 min-w-0`} aria-label={props.label}>
      <For each={props.options}>
        {([value, label, n]) => (
          <button
            type="button"
            class={`${TOOLBAR_GROUP_OPTION} ${props.value === value ? 'bg-surface-150-bg text-fg-default' : 'text-fg-tertiary hover:text-fg-default'}`}
            aria-pressed={props.value === value}
            onClick={() => props.value !== value && props.onChange(value)}
          >
            {label}
            <Show when={n !== undefined}>
              <span class="text-fg-muted font-figure text-xs">{n}</span>
            </Show>
          </button>
        )}
      </For>
    </fieldset>
  );
}

/** logdash's `CARD_COLUMNS`: a card's muted column headings over its rows. */
export const CARD_COLUMNS = 'flex h-6 shrink-0 items-center justify-between gap-3 px-3 text-xs text-fg-muted';

/** logdash's `SETTINGS_INPUT_CLASS`, on an `ld-text-input` (its `Input`, size sm) in a settings card. */
export const SETTINGS_INPUT =
  'ld-text-input bg-surface-50-bg border-surface-50-border focus:border-brand focus-within:border-brand data-error:border-error rounded-lg text-sm';

/** logdash's `Well`: a dark rounded panel on the page's card, its title (or own header) and actions over its content. */
export function Well(props: {
  title?: string;
  header?: JSX.Element;
  actions?: JSX.Element;
  label?: string;
  class?: string;
  children: JSX.Element;
}) {
  return (
    <section
      class={`bg-surface-25-bg flex min-w-0 flex-col gap-2 rounded-2xl p-2 ${props.class ?? ''}`}
      aria-label={props.label ?? props.title}
    >
      <Show when={props.header || props.title || props.actions}>
        <header class="flex h-7 shrink-0 items-center justify-between gap-3">
          <Show when={props.header} fallback={<h2 class="truncate px-3 text-[13px] font-medium">{props.title}</h2>}>
            {(header) => header()}
          </Show>
          <Show when={props.actions}>
            {(actions) => <div class="flex shrink-0 items-center gap-1">{actions()}</div>}
          </Show>
        </header>
      </Show>
      {props.children}
    </section>
  );
}

/** logdash's `SettingsCard`: a title and what it is for (red in a danger zone), actions, its items between hairlines. */
export function SettingsCard(props: {
  title: string;
  description?: JSX.Element;
  danger?: boolean;
  actions?: JSX.Element;
  class?: string;
  children: JSX.Element;
}) {
  return (
    <section class={`bg-surface-25-bg flex min-w-0 flex-col gap-1 rounded-2xl p-2 ${props.class ?? ''}`}>
      <div class="flex min-h-7 items-start justify-between gap-3 px-3 pt-1.5">
        <div class="flex min-w-0 flex-col gap-0.5">
          <h2 class={`text-[13px] font-medium ${props.danger ? 'text-error' : ''}`}>{props.title}</h2>
          <Show when={props.description}>{(d) => <p class="text-fg-muted text-[13px]">{d()}</p>}</Show>
        </div>
        <Show when={props.actions}>
          {(actions) => <div class="-mt-0.5 flex shrink-0 items-center gap-1">{actions()}</div>}
        </Show>
      </div>
      <div class="edge-between flex min-w-0 flex-col">{props.children}</div>
    </section>
  );
}

/** logdash's `SettingsCardItem`: a row of a settings card, its control on the right. */
export function SettingsItem(props: { children: JSX.Element; action?: JSX.Element }) {
  return (
    <div class="flex min-h-12 w-full items-center justify-between gap-4 px-3 py-2.5">
      <div class="min-w-0 flex-1 text-[13px]">{props.children}</div>
      <Show when={props.action}>
        {(action) => <div class="-my-1.5 flex shrink-0 items-center gap-2">{action()}</div>}
      </Show>
    </div>
  );
}

/** logdash's `EmptyState`: a title, what to do, maybe an action; `centered` in a well with nothing else. */
export function EmptyState(props: {
  title: string;
  description?: JSX.Element;
  centered?: boolean;
  class?: string;
  children?: JSX.Element;
}) {
  return (
    <div
      class={`flex flex-col ${props.centered ? 'items-center justify-center gap-2 px-6 py-10 text-center' : 'items-start gap-1'} ${props.class ?? ''}`}
    >
      <span class={props.centered ? 'font-medium' : 'text-sm'}>{props.title}</span>
      <Show when={props.description}>
        {(d) => <span class={`text-sm ${props.centered ? 'text-fg-tertiary max-w-sm' : 'text-fg-muted'}`}>{d()}</span>}
      </Show>
      <Show when={props.children}>
        {(children) => (
          <div class={`flex flex-col ${props.centered ? 'mt-2 items-center' : 'mt-3 items-start'}`}>{children()}</div>
        )}
      </Show>
    </div>
  );
}

/** logdash's `LoadingLine`: a spinner and what it waits for. */
export function LoadingLine(props: { label: string }) {
  return (
    <span class="text-fg-muted flex h-5 items-center gap-2 text-sm" role="status">
      <span class="ld-spinner size-3.5 shrink-0" aria-hidden="true" />
      {props.label}
    </span>
  );
}

/**
 * logdash's `IconButton`: a small square of an icon; `well` / `raised` for the hover of the surface it sits on; with an
 * `href` it is a link out (a new tab).
 */
export function IconButton(props: {
  label: string;
  onClick?: (e: MouseEvent) => void;
  href?: string;
  well?: boolean;
  raised?: boolean;
  danger?: boolean;
  disabled?: boolean;
  class?: string;
  children: JSX.Element;
}) {
  const hover = () =>
    props.raised
      ? 'hover:bg-surface-150-hover-bg'
      : props.well
        ? 'hover:bg-surface-25-hover-bg'
        : 'hover:bg-surface-100-hover-bg';
  const cls = () =>
    `text-fg-muted focus-visible:outline-brand transition-ink disabled:text-fg-disabled relative flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md focus-visible:outline-2 disabled:pointer-events-none ${hover()} ${props.danger ? 'hover:text-error' : 'hover:text-fg-default'} ${props.class ?? ''}`;
  return (
    <Show
      when={props.href}
      fallback={
        <button
          type="button"
          class={cls()}
          aria-label={props.label}
          title={props.label}
          disabled={props.disabled}
          onClick={(e) => props.onClick?.(e)}
        >
          {props.children}
        </button>
      }
    >
      {(href) => (
        <a
          href={href()}
          target="_blank"
          rel="noopener noreferrer"
          class={cls()}
          aria-label={props.label}
          title={props.label}
          onClick={(e) => {
            e.stopPropagation();
            props.onClick?.(e);
          }}
        >
          {props.children}
        </a>
      )}
    </Show>
  );
}

// ─── The top bar's right side: a page's own controls (logdash's `topBarState.show`) ─────────────────────────

const [toolbar, setToolbar] = createSignal<HTMLElement>();

export { setToolbar };

/** Puts its children in the top bar, right of the breadcrumbs, while the page is open. */
export function Toolbar(props: { children: JSX.Element }) {
  return (
    <Show when={toolbar()}>
      {(mount) => (
        <Portal mount={mount()} ref={(d) => d.setAttribute('class', 'flex min-w-0 flex-wrap items-center gap-2')}>
          {props.children}
        </Portal>
      )}
    </Show>
  );
}
